export const CADENCE_UNITS = [
    { value: 'MINUTE', label: 'Minute', plural: 'Minutes' },
    { value: 'HOUR', label: 'Hour', plural: 'Hours' },
    { value: 'DAY', label: 'Day', plural: 'Days' },
    { value: 'WEEK', label: 'Week', plural: 'Weeks' },
    { value: 'MONTH', label: 'Month', plural: 'Months' },
    { value: 'YEAR', label: 'Year', plural: 'Years' },
];

const NICE_CADENCE = {
    MINUTE: 'Every minute',
    HOUR: 'Hourly',
    DAY: 'Daily',
    WEEK: 'Weekly',
    MONTH: 'Monthly',
    YEAR: 'Yearly',
};

export const cadenceLabel = (value, unit) => {
    const meta = CADENCE_UNITS.find((u) => u.value === unit) || CADENCE_UNITS[2];
    if (!value || value <= 1) return NICE_CADENCE[meta.value];
    return `Every ${value} ${meta.plural.toLowerCase()}`;
};

export const WEEKDAYS = [
    { code: 'MO', short: 'Mon', label: 'Monday' },
    { code: 'TU', short: 'Tue', label: 'Tuesday' },
    { code: 'WE', short: 'Wed', label: 'Wednesday' },
    { code: 'TH', short: 'Thu', label: 'Thursday' },
    { code: 'FR', short: 'Fri', label: 'Friday' },
    { code: 'SA', short: 'Sat', label: 'Saturday' },
    { code: 'SU', short: 'Sun', label: 'Sunday' },
];

export const ORDINALS = [
    { value: 1, label: 'First' },
    { value: 2, label: 'Second' },
    { value: 3, label: 'Third' },
    { value: 4, label: 'Fourth' },
    { value: -1, label: 'Last' },
];

const WD_INDEX = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
const WEEK_S = 604800;
const DAY_S = 86400;
const HOUR_S = 3600;
const MIN_S = 60;
const DAY_MS = 86400000;
const OCCURRENCE_MAX = 500;

export const unitSupportsWeekdays = (unit) => unit === 'WEEK';
export const unitSupportsMonthly = (unit) => unit === 'MONTH';

export const secondsToParts = (total) => {
    let s = Math.max(0, Math.floor(total ?? 0));
    const weeks = Math.floor(s / WEEK_S);
    s -= weeks * WEEK_S;
    const days = Math.floor(s / DAY_S);
    s -= days * DAY_S;
    const hours = Math.floor(s / HOUR_S);
    s -= hours * HOUR_S;
    const minutes = Math.floor(s / MIN_S);
    s -= minutes * MIN_S;
    return { weeks, days, hours, minutes, seconds: s };
};

export const partsToSeconds = (p) => (p.weeks ?? 0) * WEEK_S + (p.days ?? 0) * DAY_S
    + (p.hours ?? 0) * HOUR_S + (p.minutes ?? 0) * MIN_S + (p.seconds ?? 0);

export const formatDuration = (total) => {
    if (!total || total <= 0) return 'No set duration';
    const p = secondsToParts(total);
    return [
        p.weeks && `${p.weeks}w`,
        p.days && `${p.days}d`,
        p.hours && `${p.hours}h`,
        p.minutes && `${p.minutes}m`,
        p.seconds && `${p.seconds}s`,
    ].filter(Boolean).join(' ');
};

const FALLBACK_TZS = ['UTC', 'America/Los_Angeles', 'America/New_York', 'Europe/London', 'Europe/Berlin',
    'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'];

export const listTimezones = () => {
    try {
        const zones = Intl.supportedValuesOf?.('timeZone');
        if (Array.isArray(zones) && zones.length) return zones.includes('UTC') ? zones : ['UTC', ...zones];
    } catch {
        return FALLBACK_TZS;
    }
    return FALLBACK_TZS;
};

/**
 * The timezone picker's options with the saved zone always among them. Browsers name some zones differently
 * (Asia/Calcutta vs Asia/Kolkata), and a search box shows nothing at all for a value that is not an option.
 */
export const zoneOptions = (zones, saved) => {
    const options = (zones || []).map((tz) => ({ value: tz, label: tz }));
    return !saved || options.some((o) => o.value === saved) ? options : [{ value: saved, label: saved }, ...options];
};

export const guessTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
};

const wallClockInZone = (utcMs, zone) => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(new Date(utcMs));
        const get = (t) => Number(parts.find((x) => x.type === t)?.value);
        return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    } catch {
        return null;
    }
};

export const parseLocal = (s) => {
    const m = s ? /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s) : null;
    if (!m) return null;
    return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), h: Number(m[4]), mi: Number(m[5]) };
};

export const localToInstant = (local, zone) => {
    const p = parseLocal(local);
    if (!p) return null;
    const asUTC = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, 0);
    const shown1 = wallClockInZone(asUTC, zone);
    if (shown1 == null) return asUTC;
    let t = asUTC - (shown1 - asUTC);
    const shown2 = wallClockInZone(t, zone);
    if (shown2 != null && shown2 - t !== shown1 - asUTC) t = asUTC - (shown2 - t);
    return t;
};

export const scheduleStartInstant = (s, viewerZone) => {
    if (!s.startLocal) return null;
    return localToInstant(s.startLocal, s.tzMode === 'FLOATING' ? viewerZone : s.timezone || 'UTC');
};

const daysInMonthUTC = (year, monthIndex) => new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const fmtWc = (wcMs) => {
    const d = new Date(wcMs);
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
};

const WEEKDAY_ORDER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const shiftWeekday = (code, days) => {
    const i = WEEKDAY_ORDER.indexOf(code);
    return i < 0 ? code : WEEKDAY_ORDER[(((i + days) % 7) + 7) % 7];
};

/**
 * Moves a fixed-timezone schedule to another timezone without moving when it happens: the start is the same moment,
 * shown as that zone's wall clock. Midnight in Asia/Calcutta is 18:30 the day before in UTC, so where the date
 * crosses midnight the chosen weekdays and the monthly day move with it, or the runs would land a day off.
 *
 * A schedule in players' local time, or one with no start yet, only takes the new zone: there is no moment to keep.
 */
export const rezoneSchedule = (s, toZone) => {
    if (s.tzMode !== 'FIXED' || !s.startLocal) return { ...s, timezone: toZone };
    const instant = localToInstant(s.startLocal, s.timezone || 'UTC');
    const wall = instant == null ? null : wallClockInZone(instant, toZone);
    if (wall == null) return { ...s, timezone: toZone };
    const from = parseLocal(s.startLocal);
    const startLocal = fmtWc(wall) + s.startLocal.slice(16);
    const to = parseLocal(startLocal);
    const dayShift = Math.round((Date.UTC(to.y, to.mo - 1, to.d) - Date.UTC(from.y, from.mo - 1, from.d)) / DAY_MS);
    const rec = s.recurrence;
    if (!dayShift || !rec) return { ...s, timezone: toZone, startLocal };
    const monthly = rec.monthly && (rec.monthly.mode === 'ORDINAL_WEEKDAY'
        ? { ...rec.monthly, weekday: shiftWeekday(rec.monthly.weekday, dayShift) }
        : { ...rec.monthly, day: ((((rec.monthly.day || 1) - 1 + dayShift) % 31) + 31) % 31 + 1 });
    return {
        ...s,
        timezone: toZone,
        startLocal,
        recurrence: {
            ...rec,
            ...(Array.isArray(rec.byWeekday) ? { byWeekday: rec.byWeekday.map((c) => shiftWeekday(c, dayShift)) } : {}),
            ...(monthly ? { monthly } : {}),
        },
    };
};

const monthlyOccurrenceWc = (y, m, monthly, h, min) => {
    if (monthly.mode === 'DAY_OF_MONTH') {
        return Date.UTC(y, m, Math.min(Math.max(1, monthly.day ?? 1), daysInMonthUTC(y, m)), h, min);
    }
    const wd = WD_INDEX[monthly.weekday ?? 'MO'];
    const ord = monthly.ordinal ?? 1;
    const last = daysInMonthUTC(y, m);
    if (ord === -1) {
        for (let day = last; day >= 1; day -= 1) {
            if (new Date(Date.UTC(y, m, day)).getUTCDay() === wd) return Date.UTC(y, m, day, h, min);
        }
        return null;
    }
    let count = 0;
    for (let day = 1; day <= last; day += 1) {
        if (new Date(Date.UTC(y, m, day)).getUTCDay() === wd) {
            count += 1;
            if (count === ord) return Date.UTC(y, m, day, h, min);
        }
    }
    return null;
};

const simpleStepWc = (unit, k, interval, a) => {
    switch (unit) {
        case 'MINUTE': return a.wc + interval * k * 60000;
        case 'HOUR': return a.wc + interval * k * 3600000;
        case 'DAY': return Date.UTC(a.y, a.mo, a.d + interval * k, a.h, a.mi);
        case 'WEEK': return Date.UTC(a.y, a.mo, a.d + 7 * interval * k, a.h, a.mi);
        case 'MONTH': {
            const target = a.mo + interval * k;
            const y = a.y + Math.floor(target / 12);
            const m = ((target % 12) + 12) % 12;
            return Date.UTC(y, m, Math.min(a.d, daysInMonthUTC(y, m)), a.h, a.mi);
        }
        case 'YEAR': {
            const y = a.y + interval * k;
            return Date.UTC(y, a.mo, Math.min(a.d, daysInMonthUTC(y, a.mo)), a.h, a.mi);
        }
        default: return a.wc;
    }
};

export const expandOccurrences = (schedule, anchorMs, viewerZone, capDays = 90) => {
    if (!Number.isFinite(anchorMs)) return [];
    if (schedule.kind === 'ONE_TIME') return [anchorMs];
    const rec = schedule.recurrence;
    const interval = Math.max(1, Math.floor(schedule.intervalValue) || 1);
    const unit = schedule.intervalUnit;
    const zone = schedule.tzMode === 'FLOATING' ? viewerZone : schedule.timezone || 'UTC';

    let capMs = anchorMs + Math.max(1, Math.floor(capDays)) * DAY_MS;
    if (rec?.end?.type === 'UNTIL') {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(rec.end.date || '');
        const until = m ? localToInstant(`${m[1]}-${m[2]}-${m[3]}T23:59`, zone) : null;
        if (until != null) capMs = Math.min(capMs, until);
    }
    const countCap = rec?.end?.type === 'COUNT'
        ? Math.min(OCCURRENCE_MAX, Math.max(1, rec.end.count)) : OCCURRENCE_MAX;

    const anchorWc = wallClockInZone(anchorMs, zone);
    if (anchorWc == null) return [anchorMs];
    const ad = new Date(anchorWc);
    const a = {
        wc: anchorWc, y: ad.getUTCFullYear(), mo: ad.getUTCMonth(), d: ad.getUTCDate(),
        h: ad.getUTCHours(), mi: ad.getUTCMinutes(),
    };
    const toInstant = (wcMs) => localToInstant(fmtWc(wcMs), zone);
    const out = [];

    if (unit === 'WEEK' && rec?.byWeekday?.length) {
        const days = [...new Set(rec.byWeekday.map((c) => WD_INDEX[c]))];
        const baseMonday = a.d - ((ad.getUTCDay() + 6) % 7);
        for (let k = 0; out.length < countCap && k < 2000; k += 1) {
            const wsDay = baseMonday + 7 * interval * k;
            const wsT = toInstant(Date.UTC(a.y, a.mo, wsDay, a.h, a.mi));
            if (wsT == null || wsT > capMs) break;
            days.forEach((wd) => {
                const t = toInstant(Date.UTC(a.y, a.mo, wsDay + ((wd + 6) % 7), a.h, a.mi));
                if (t != null && t >= anchorMs && t <= capMs) out.push(t);
            });
        }
        return out.sort((x, y) => x - y).slice(0, countCap);
    }

    if (unit === 'MONTH' && rec?.monthly) {
        for (let k = 0; out.length < countCap && k < 2000; k += 1) {
            const target = a.mo + interval * k;
            const occWc = monthlyOccurrenceWc(a.y + Math.floor(target / 12), ((target % 12) + 12) % 12, rec.monthly, a.h, a.mi);
            const t = occWc == null ? null : toInstant(occWc);
            if (t != null && t > capMs) break;
            if (t != null && t >= anchorMs) out.push(t);
        }
        return out.slice(0, countCap);
    }

    for (let k = 0; out.length < countCap && k < 200000; k += 1) {
        const t = toInstant(simpleStepWc(unit, k, interval, a));
        if (t == null || t > capMs) break;
        if (t >= anchorMs) out.push(t);
    }
    return out.slice(0, countCap);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatLocal = (s) => {
    const p = parseLocal(s);
    if (!p) return '—';
    return `${MONTHS[Math.min(11, Math.max(0, p.mo - 1))]} ${p.d}, ${p.y} · ${pad2(p.h)}:${pad2(p.mi)}`;
};

export const formatLocalDate = (s) => {
    const m = s ? /^(\d{4})-(\d{2})-(\d{2})/.exec(s) : null;
    if (!m) return '—';
    return `${MONTHS[Math.min(11, Math.max(0, Number(m[2]) - 1))]} ${Number(m[3])}, ${m[1]}`;
};

// "YYYY-MM-DD" to and from the local-midnight Date that the shared DateTimePicker's date variants take and give.
export const dateFromYmd = (s) => {
    const m = s ? /^(\d{4})-(\d{2})-(\d{2})/.exec(s) : null;
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

export const ymdOf = (d) => (d instanceof Date && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    : '');

export const tzLabel = (s) => (s.tzMode === 'FLOATING' ? "player's local time" : s.timezone || 'UTC');

// A new event has no schedule until someone sets one: nothing about when it runs is stored at creation.
export const hasSchedule = (event) => !!(event?.scheduleKind || event?.startLocal);

// Where the schedule editor starts for an event without one. Only its controls get a value; when it starts and how
// long each run lasts stay empty until they're picked, and nothing is stored until Save.
export const blankSchedule = () => ({
    kind: 'RECURRING',
    intervalValue: 1,
    intervalUnit: 'WEEK',
    startLocal: null,
    tzMode: 'FIXED',
    timezone: guessTimezone(),
    durationSeconds: null,
    recurrence: { end: { type: 'NEVER' } },
});

export const scheduleFrom = (event) => ({
    kind: event?.scheduleKind || 'RECURRING',
    intervalValue: event?.intervalValue || 1,
    intervalUnit: event?.intervalUnit || 'WEEK',
    startLocal: event?.startLocal || null,
    tzMode: event?.tzMode || 'FIXED',
    timezone: event?.timezone || null,
    durationSeconds: event?.durationSeconds || null,
    recurrence: event?.recurrence || null,
});

const MAX_DURATION_S = 366 * 24 * 3600;
const clampInt = (n, lo, hi, dflt) => {
    const v = Math.floor(Number(n));
    return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : dflt;
};
const isWeekday = (c) => WEEKDAYS.some((d) => d.code === c);

const normalizeEnd = (e) => {
    if (e?.type === 'COUNT') return { type: 'COUNT', count: clampInt(e.count, 1, 10000, 1) };
    if (e?.type === 'UNTIL' && /^\d{4}-\d{2}-\d{2}$/.test(e.date || '')) return { type: 'UNTIL', date: e.date };
    return { type: 'NEVER' };
};

export const normalizeRecurrence = (unit, raw) => {
    const input = raw && typeof raw === 'object' ? raw : {};
    const out = {};
    if (unitSupportsWeekdays(unit) && Array.isArray(input.byWeekday)) {
        const days = WEEKDAYS.map((d) => d.code).filter((c) => input.byWeekday.includes(c));
        if (days.length) out.byWeekday = days;
    }
    if (unitSupportsMonthly(unit) && input.monthly && typeof input.monthly === 'object') {
        const m = input.monthly;
        out.monthly = m.mode === 'ORDINAL_WEEKDAY'
            ? { mode: 'ORDINAL_WEEKDAY', ordinal: [1, 2, 3, 4, -1].includes(Number(m.ordinal)) ? Number(m.ordinal) : 1, weekday: isWeekday(m.weekday) ? m.weekday : 'MO' }
            : { mode: 'DAY_OF_MONTH', day: clampInt(m.day, 1, 31, 1) };
    }
    out.end = normalizeEnd(input.end);
    return out;
};

export const scheduleToEvent = (s) => {
    const dur = Number(s.durationSeconds);
    return {
        scheduleKind: s.kind,
        intervalValue: Math.max(1, Math.floor(s.intervalValue) || 1),
        intervalUnit: s.intervalUnit,
        startLocal: s.startLocal,
        tzMode: s.tzMode,
        timezone: s.timezone || (s.tzMode === 'FIXED' ? 'UTC' : null),
        durationSeconds: Number.isFinite(dur) && dur > 0 ? Math.min(MAX_DURATION_S, Math.floor(dur)) : 0,
        recurrence: s.kind === 'RECURRING' ? normalizeRecurrence(s.intervalUnit, s.recurrence) : {},
    };
};

const weekdayList = (codes) => codes.map((c) => WEEKDAYS.find((d) => d.code === c)?.short ?? c).join(', ');

export const summarizeSchedule = (s) => {
    const parts = [];
    if (s.kind === 'ONE_TIME') {
        parts.push(`Once · ${formatLocal(s.startLocal)}`);
    } else {
        let base = cadenceLabel(s.intervalValue, s.intervalUnit);
        const r = s.recurrence;
        if (s.intervalUnit === 'WEEK' && r?.byWeekday?.length) {
            base += ` on ${weekdayList(r.byWeekday)}`;
        } else if (s.intervalUnit === 'MONTH' && r?.monthly) {
            if (r.monthly.mode === 'ORDINAL_WEEKDAY') {
                const ord = ORDINALS.find((o) => o.value === (r.monthly.ordinal ?? 1))?.label ?? 'First';
                const wd = WEEKDAYS.find((d) => d.code === (r.monthly.weekday ?? 'MO'))?.label ?? 'Monday';
                base += ` on the ${ord.toLowerCase()} ${wd}`;
            } else if (r.monthly.day) {
                base += ` on day ${r.monthly.day}`;
            }
        }
        parts.push(base);
        if (s.startLocal) parts.push(`from ${formatLocal(s.startLocal)}`);
    }
    if (s.durationSeconds && s.durationSeconds > 0) parts.push(`for ${formatDuration(s.durationSeconds)}`);
    if (s.kind === 'RECURRING') {
        const end = s.recurrence?.end;
        if (end?.type === 'COUNT') parts.push(`${end.count}×`);
        else if (end?.type === 'UNTIL') parts.push(`until ${formatLocalDate(end.date)}`);
    }
    if (s.kind === 'ONE_TIME' || s.startLocal || s.tzMode === 'FLOATING') parts.push(tzLabel(s));
    return parts.join(' · ');
};

const fmtInZone = (ms, zone, opts) => {
    try {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).format(new Date(ms));
    } catch {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(new Date(ms));
    }
};

export const formatOccurrence = (ms, zone) => fmtInZone(ms, zone, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

// The zone an event's dates are shown in. A "player's local time" event has no zone of its own; its dates come
// from the upcoming feed, which says which zone it worked them out in.
export const eventZone = (event, floatingZone = 'UTC') => {
    if (event?.tzMode === 'FIXED' && event?.timezone) return event.timezone;
    return event?.tzMode === 'FLOATING' && floatingZone ? floatingZone : 'UTC';
};

export const intervalSeconds = (s) => {
    if (s.kind !== 'RECURRING') return null;
    const unit = { MINUTE: 60, HOUR: 3600, DAY: 86400, WEEK: 604800, MONTH: 2592000, YEAR: 31536000 }[s.intervalUnit];
    return unit ? Math.max(1, Math.floor(s.intervalValue) || 1) * unit : null;
};

export const previewDays = (s, wanted) => {
    const u = String(s.intervalUnit);
    return Math.min(wanted, u === 'MINUTE' ? 2 : u === 'HOUR' ? 14 : wanted);
};

export const previewDates = (s, viewer, n = 5) => {
    const anchorMs = scheduleStartInstant(s, viewer);
    if (anchorMs == null || !Number.isFinite(anchorMs)) return [];
    const zone = s.tzMode === 'FIXED' && s.timezone ? s.timezone : viewer;
    const all = expandOccurrences(s, anchorMs, viewer, previewDays(s, 400));
    const now = Date.now();
    const future = all.filter((d) => d >= now);
    return (future.length ? future : all).slice(0, n).map((ms) => ({ ms, zone }));
};

// Days to look ahead for every kind of gap: a few cycles, a leap cycle of months (a 28-day February), a leap cycle of years.
const gapWindowDays = (s) => {
    const n = Math.max(1, Math.floor(s.intervalValue) || 1);
    switch (s.intervalUnit) {
        case 'MINUTE': return Math.ceil((3 * n) / 1440) + 1;
        case 'HOUR': return Math.ceil((3 * n) / 24) + 1;
        case 'DAY': return 3 * n + 1;
        case 'WEEK': return 21 * n + 1;
        case 'MONTH': return 49 * 31 * n;
        default: return 4 * 366 * n + 1;
    }
};

// Shortest and longest time between two starts, in clock time (worked out in UTC, so daylight saving never moves them).
export const runGaps = (s) => {
    if (s?.kind !== 'RECURRING' || !s.startLocal) return null;
    const anchor = localToInstant(s.startLocal, 'UTC');
    if (anchor == null) return null;
    const starts = expandOccurrences({ ...s, tzMode: 'FIXED', timezone: 'UTC' }, anchor, 'UTC', gapWindowDays(s));
    let min = Infinity;
    let max = 0;
    for (let i = 1; i < starts.length; i += 1) {
        const gap = (starts[i] - starts[i - 1]) / 1000;
        if (gap > 0) {
            min = Math.min(min, gap);
            max = Math.max(max, gap);
        }
    }
    return Number.isFinite(min) ? { min, max } : null;
};

// Runs overlap when one lasts longer than the shortest gap; ending exactly as the next starts is back to back.
export const runOverlap = (s) => {
    const duration = Number(s?.durationSeconds);
    if (!(duration > 0)) return null;
    const gaps = runGaps(s);
    return gaps && duration > gaps.min ? { duration, gap: gaps.min, uneven: gaps.max > gaps.min } : null;
};

// The longest a run can last without reaching the next start; the plain cadence when there's no second start.
export const longestRun = (s) => {
    const gaps = runGaps(s);
    if (gaps) return { seconds: gaps.min, longest: gaps.max, uneven: gaps.max > gaps.min };
    const interval = intervalSeconds(s);
    return interval == null ? null : { seconds: interval, longest: interval, uneven: false };
};

const apart = (o) => `${o.uneven ? 'some runs start only' : 'runs start'} ${formatDuration(o.gap)} apart`;

export const overlapWarning = (o) => `Runs would overlap: each lasts ${formatDuration(o.duration)}, but ${apart(o)}.`;

export const overlapError = (o) => {
    const gaps = apart(o);
    return `Each run has to end before the next one starts. ${gaps.charAt(0).toUpperCase()}${gaps.slice(1)}, so a run can last at most ${formatDuration(o.gap)}.`;
};

export const savedOverlapNote = (o) => `Runs overlap: each lasts ${formatDuration(o.duration)}, but ${apart(o)}, so a date can’t go out while an earlier one still runs. Edit the schedule to fix it.`;

export const inheritedOverlapNote = (o) => `The event’s schedule makes runs overlap: each lasts ${formatDuration(o.duration)}, but ${apart(o)}. Fix it in the event’s schedule.`;
